// Recruiter-facing copy for the consumption module's package-limit errors.
//
// When a metered call exceeds the package limit, the consumption module rejects it
// with the artifact's raw *location* as the message:
//
//   { "message": "selection_counts.both_count limit exceeded",
//     "error_code": "ARTIFACT_LIMIT_EXCEEDED",
//     "artifact": { "artifact_id": 55, "artifact_name": null,
//                   "location": "selection_counts.both_count",
//                   "limit_type": "limit" } }
//
// That location is a package-configuration path, not something a recruiter can act
// on, and every artifact reports a different one — so nothing here may show it
// verbatim. Each known location gets tuned copy below; anything else (new artifacts
// get added to the package over time) falls back to the IAM artifact name, then to a
// humanised location. The raw text is preserved on the error object as
// `raw_message` for the console / bug reports.
export const ARTIFACT_LIMIT_ERROR_CODE = 'ARTIFACT_LIMIT_EXCEEDED';
// Keyed by `artifact.location`. Each value completes the sentence
// "Your plan's limit for <label> has been reached." — so keep them plural nouns.
//
// The selection_counts.* keys are the one-hot per-option counters the JD API
// reports (see `JDSerializer.get_selection_counts` in ai-interview-be); the root
// level keys are the per-request scheduling counters.
const ARTIFACT_LABELS = {
    // interview_type
    'selection_counts.coding_count': 'coding interviews',
    'selection_counts.non_coding_count': 'non-coding interviews',
    'selection_counts.both_count': 'coding + non-coding ("Both") interviews',
    // interview_level
    'selection_counts.entry_count': 'entry-level interviews',
    'selection_counts.mid_count': 'mid-level interviews',
    'selection_counts.senior_count': 'senior-level interviews',
    // interview_taken_by — labelled with the wording the setup form uses
    'selection_counts.taken_by_openai_count': 'Realtime-mode interviews',
    'selection_counts.taken_by_azure_count': 'Cost-optimized-mode interviews',
    // voice_config.gender
    'selection_counts.voice_male_count': 'interviews with a male voice',
    'selection_counts.voice_female_count': 'interviews with a female voice',
    // toggles
    'selection_counts.ask_dynamic_question_enabled_count': 'interviews with dynamic questions turned on',
    'selection_counts.ask_dynamic_question_disabled_count': 'interviews with dynamic questions turned off',
    'selection_counts.followup_question_enabled_count': 'interviews with follow-up questions turned on',
    'selection_counts.followup_question_disabled_count': 'interviews with follow-up questions turned off',
    // quantities
    'selection_counts.coding_questions_count': 'coding questions',
    'selection_counts.coding_questions_total_minutes': 'coding-question minutes',
    'selection_counts.interview_duration_minutes': 'interview minutes',
    // scheduling (reported per request by schedule / reschedule)
    scheduled_interview_count: 'scheduled interviews',
    rescheduled_interview_count: 'rescheduled interviews',
    slot_followup_count: 'candidate slot retries',
    // VA-27685: the same option counters, now reported by the SCHEDULING endpoint
    // instead of the JD-config one. Metering them on `PATCH /jd/{id}/update` counted
    // a configuration rather than an interview, so a JD set to "coding" consumed one
    // unit however many candidates were then scheduled from it.
    //
    // Deliberately the SAME copy as the selection_counts.* entry above each one: the
    // artifact is the same allowance and the recruiter must see the same sentence —
    // only the moment it is counted has changed. Without these the lookup would miss
    // and fall through to `labelFromLocation`, degrading "coding interviews" to
    // "scheduled coding".
    //
    // Both spellings are kept while IAM re-registers: whichever location the rejection
    // arrives with, the toast is identical.
    scheduled_coding_count: 'coding interviews',
    scheduled_non_coding_count: 'non-coding interviews',
    scheduled_both_count: 'coding + non-coding ("Both") interviews',
    scheduled_entry_count: 'entry-level interviews',
    scheduled_mid_count: 'mid-level interviews',
    scheduled_senior_count: 'senior-level interviews',
    scheduled_taken_by_openai_count: 'Realtime-mode interviews',
    scheduled_taken_by_azure_count: 'Cost-optimized-mode interviews',
    scheduled_voice_male_count: 'interviews with a male voice',
    scheduled_voice_female_count: 'interviews with a female voice',
    scheduled_ask_dynamic_question_enabled_count: 'interviews with dynamic questions turned on',
    scheduled_ask_dynamic_question_disabled_count: 'interviews with dynamic questions turned off',
    scheduled_followup_question_enabled_count: 'interviews with follow-up questions turned on',
    scheduled_followup_question_disabled_count: 'interviews with follow-up questions turned off',
};
// "Coding + Non-Coding Interview Count" -> "coding + non-coding interview".
// IAM artifact names are title-cased and end in "Count"/"Counts"; strip that so the
// name reads as a noun inside the sentence.
const labelFromArtifactName = (name) => name
    .trim()
    .replace(/\s*counts?$/i, '')
    .toLowerCase();
// "selection_counts.ask_dynamic_question_enabled_count" -> "ask dynamic question enabled".
// Last-resort: an artifact we have no copy for yet still reads as words, never as a path.
const labelFromLocation = (location) => (location.split('.').pop() || location)
    .replace(/_counts?$/i, '')
    .replace(/non_coding/gi, 'non-coding')
    .replace(/_/g, ' ')
    .trim();
const getArtifact = (err) => { var _a, _b, _c; return (err === null || err === void 0 ? void 0 : err.artifact) || ((_a = err === null || err === void 0 ? void 0 : err.data) === null || _a === void 0 ? void 0 : _a.artifact) || ((_c = (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.artifact) || null; };
const getErrorCode = (err) => { var _a, _b, _c; return String((err === null || err === void 0 ? void 0 : err.error_code) || ((_a = err === null || err === void 0 ? void 0 : err.data) === null || _a === void 0 ? void 0 : _a.error_code) || ((_c = (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.error_code) || ''); };
/** True when this rejected response is a package/artifact limit rejection. */
export const isArtifactLimitError = (err) => getErrorCode(err) === ARTIFACT_LIMIT_ERROR_CODE;
/**
 * Recruiter-facing sentence for an artifact-limit error, or `null` when the error
 * is something else.
 */
export const getArtifactLimitMessage = (err) => {
    if (!isArtifactLimitError(err))
        return null;
    const artifact = getArtifact(err);
    const location = String((artifact === null || artifact === void 0 ? void 0 : artifact.location) || '').trim();
    const artifactName = String((artifact === null || artifact === void 0 ? void 0 : artifact.artifact_name) || '').trim();
    const label = ARTIFACT_LABELS[location] ||
        (artifactName ? labelFromArtifactName(artifactName) : '') ||
        (location ? labelFromLocation(location) : '');
    // A limit we cannot name at all — still never leak the raw location.
    if (!label) {
        return "You've reached a usage limit included in your plan. Please upgrade your plan to continue.";
    }
    // `limit_type` is "limit" for a flat package allowance; a per-period allowance
    // ("frequency"/"rate") resets, so it still says "for this period" — the recruiter
    // needs to know the allowance is not gone for good.
    //
    // But the call to action is upgrading, and only upgrading. "Please try again later"
    // tells a recruiter with work to do right now to come back tomorrow, which puts the
    // cost of a plan limit onto them and reads as an apology for the product. The
    // backend takes the same line deliberately — see `_shortfall_message` in
    // ai-interview-be/interview/services/consumption_balance.py.
    const limitType = String((artifact === null || artifact === void 0 ? void 0 : artifact.limit_type) || 'limit').toLowerCase();
    const isPerPeriod = limitType.includes('frequency') || limitType.includes('rate');
    return isPerPeriod
        ? `Your plan's limit for ${label} has been reached for this period. Please upgrade your plan to continue.`
        : `Your plan's limit for ${label} has been reached. Please upgrade your plan to continue.`;
};
/**
 * Rewrite an artifact-limit body in place so *every* existing caller — the many
 * `err?.message || err?.error || 'Failed!'` toasts — shows the friendly sentence
 * without each one having to know about artifact limits. The raw text moves to
 * `raw_message` / `raw_error`; other errors pass through untouched.
 */
export const normalizeArtifactLimitError = (body) => {
    if (!body || typeof body !== 'object' || !isArtifactLimitError(body))
        return body;
    const friendly = getArtifactLimitMessage(body);
    if (!friendly)
        return body;
    body.raw_message = body.message;
    body.raw_error = body.error;
    body.display_message = friendly;
    body.message = friendly;
    body.error = friendly;
    return body;
};
/**
 * Message to show for a failed request: the friendly artifact-limit sentence when
 * that is what happened, else whatever the API said, else the caller's fallback.
 * For call sites whose current fallback would otherwise mask the server's message.
 */
export const getApiErrorMessage = (err, fallback) => {
    var _a, _b, _c;
    const artifactMessage = getArtifactLimitMessage(err);
    if (artifactMessage)
        return artifactMessage;
    // A native JS error (a TypeError from a `.then` callback, an aborted fetch) is a
    // programming/transport failure, and its `.message` is developer text — "Cannot
    // read properties of undefined". Only API response bodies get read out loud.
    if (err instanceof Error)
        return fallback;
    const serverMessage = (err === null || err === void 0 ? void 0 : err.display_message) ||
        (err === null || err === void 0 ? void 0 : err.message) ||
        (err === null || err === void 0 ? void 0 : err.error) ||
        ((_a = err === null || err === void 0 ? void 0 : err.data) === null || _a === void 0 ? void 0 : _a.message) ||
        ((_c = (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message);
    return typeof serverMessage === 'string' && serverMessage.trim() ? serverMessage : fallback;
};
